/**
 * TemplateForm — create/edit dialog contents for Meta templates (Story 7.7).
 *
 * Works in two modes:
 *   - `mode='create'` — all fields editable, `metaTemplateName` required.
 *   - `mode='edit'`   — `metaTemplateName` locked (Meta does not support
 *                       renaming approved templates without resubmission);
 *                       other fields editable.
 *
 * Validation:
 *   - `metaTemplateName` regex `^[a-z0-9_]{1,64}$` — enforced client-side so
 *     operators see the error before a 400 from the server. The server
 *     enforces the same regex via TypeBox (see `templates.schemas.ts`).
 *   - `variables` shape is guaranteed by `VariablesEditor` (table-based since
 *     Story 7.15 UX revamp — no more JSON parsing on the client).
 *
 * The form is uncontrolled-ish: parent owns the initial template, this
 * component owns the intermediate state and calls `onSubmit` with the final
 * payload when valid.
 */

import { useMemo, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TemplatePreview } from '@/components/templates/TemplatePreview';
import { VariablesEditor, type TemplateVariable } from '@/components/templates/VariablesEditor';

export type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
export type TemplateStatus = 'pending' | 'approved' | 'rejected' | 'paused';
export type TemplatePurpose = 'initial_contact' | 'callback_resume' | 'reengagement' | 'converted';

export type TemplateButtonType = 'URL' | 'QUICK_REPLY' | 'AI_RESPONSE';

export interface TemplateUrlButtonValue {
  type: 'URL';
  text: string;
  url: string;
}

export interface TemplateQuickReplyButtonValue {
  type: 'QUICK_REPLY';
  text: string;
  linkedTemplateId: string;
}

/**
 * 2026-08-03 (Story 9.11c) — botão que aciona a IA (texto livre, guiado por
 * `guidanceText`) em vez de encadear outro template. `id` nunca é exposto na
 * UI (o admin não escolhe/edita) — só existe aqui pra ROUND-TRIP: ao editar
 * um template já salvo, preserva o `id` já atribuído pelo backend, senão
 * qualquer edição não relacionada a botões geraria um `id` novo a cada save,
 * invalidando silenciosamente botões já entregues a clientes reais.
 */
export interface TemplateAiResponseButtonValue {
  type: 'AI_RESPONSE';
  text: string;
  guidanceText: string;
  id?: string;
}

export type TemplateButtonValue =
  | TemplateUrlButtonValue
  | TemplateQuickReplyButtonValue
  | TemplateAiResponseButtonValue;

export interface TemplateFormValue {
  metaTemplateName: string;
  languageCode: string;
  category: TemplateCategory | null;
  status: TemplateStatus;
  contentPreview: string | null;
  variables: TemplateVariable[];
  /** Story 7.28 — botões URL estáticos (0-2). */
  buttons: TemplateButtonValue[];
  purpose: TemplatePurpose;
}

export const META_TEMPLATE_NAME_RE = /^[a-z0-9_]{1,64}$/;

/** Story 7.28 — limites Meta para botões URL (espelham o backend). */
export const URL_BUTTON_MAX_COUNT = 2;
export const URL_BUTTON_TEXT_MAX_LEN = 25;
// Story 7.28 — case-sensitive lowercase `https://`, alinhado com o pattern
// TypeBox do backend (`^https://`) pra não divergir (FE dizia válido para
// `HTTPS://` mas a API retornava 400). Meta exige https lowercase.
export const BUTTON_URL_RE = /^https:\/\//;
/** Story 9.11c — limite de tamanho do texto-guia de um botão AI_RESPONSE (espelha o backend). */
export const AI_RESPONSE_GUIDANCE_MAX_LEN = 1000;

/**
 * Valida um botão isolado. Retorna erros de campo relevantes para cada tipo.
 */
function validateButton(button: TemplateButtonValue): {
  text: string | null;
  url: string | null;
  linkedTemplateId: string | null;
  guidanceText: string | null;
} {
  let textError: string | null = null;
  if (button.text.trim().length === 0) {
    textError = 'Texto é obrigatório.';
  } else if (button.text.length > URL_BUTTON_TEXT_MAX_LEN) {
    textError = `Máximo ${URL_BUTTON_TEXT_MAX_LEN} caracteres.`;
  }
  let urlError: string | null = null;
  let linkedTemplateIdError: string | null = null;
  let guidanceTextError: string | null = null;
  if (button.type === 'URL') {
    if (button.url.trim().length === 0) {
      urlError = 'URL é obrigatória.';
    } else if (!BUTTON_URL_RE.test(button.url)) {
      urlError = 'URL deve começar com https://.';
    }
  } else if (button.type === 'QUICK_REPLY') {
    if (!button.linkedTemplateId) {
      linkedTemplateIdError = 'Selecione o template a ser enviado.';
    }
  } else {
    if (button.guidanceText.trim().length === 0) {
      guidanceTextError = 'Texto-guia é obrigatório.';
    } else if (button.guidanceText.length > AI_RESPONSE_GUIDANCE_MAX_LEN) {
      guidanceTextError = `Máximo ${AI_RESPONSE_GUIDANCE_MAX_LEN} caracteres.`;
    }
  }
  return {
    text: textError,
    url: urlError,
    linkedTemplateId: linkedTemplateIdError,
    guidanceText: guidanceTextError,
  };
}

/**
 * Story 7.26 — placeholder detector that mirrors the backend regex
 * (`/\{\{\s*(\d+)\s*\}\}/g`). Tolerates whitespace inside braces so the form
 * never disagrees with the server about what counts as a variable.
 */
const PLACEHOLDER_RE = /\{\{\s*(\d+)\s*\}\}/g;

function extractPlaceholderPositions(text: string | null | undefined): number[] {
  if (!text) return [];
  const set = new Set<number>();
  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    const n = Number(match[1]);
    if (Number.isInteger(n) && n >= 1) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

function findMissingExamplePositions(
  body: string | null | undefined,
  variables: TemplateVariable[],
): number[] {
  const positions = extractPlaceholderPositions(body);
  if (positions.length === 0) return [];
  return positions.filter((pos) => {
    const v = variables.find((x) => x.position === pos);
    const example = v?.example;
    return typeof example !== 'string' || example.trim().length === 0;
  });
}

export const DEFAULT_FORM_VALUE: TemplateFormValue = {
  metaTemplateName: '',
  languageCode: 'pt_BR',
  category: 'MARKETING',
  status: 'pending',
  contentPreview: '',
  variables: [],
  buttons: [],
  purpose: 'initial_contact',
};

export interface TemplateSummary {
  id: string;
  metaTemplateName: string;
}

interface Props {
  mode: 'create' | 'edit';
  initial?: TemplateFormValue;
  submitting?: boolean;
  /** Lista de templates disponíveis para vincular em botões QUICK_REPLY. */
  availableTemplates?: TemplateSummary[];
  /** ID do template sendo editado — excluído da lista de seleção QUICK_REPLY. */
  currentTemplateId?: string;
  /**
   * Story 7.25 — when true, `languageCode` is rendered disabled because Meta
   * does not allow relanguaging a template after the first submit. Parent
   * passes the template's `submittedToMeta` flag. Defaults to false so the
   * existing create flow keeps working without changes.
   */
  submittedToMeta?: boolean;
  onSubmit: (next: TemplateFormValue) => void;
  onCancel: () => void;
}

const CATEGORY_OPTIONS: TemplateCategory[] = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const STATUS_OPTIONS: TemplateStatus[] = ['pending', 'approved', 'rejected', 'paused'];
const PURPOSE_OPTIONS: TemplatePurpose[] = [
  'initial_contact',
  'callback_resume',
  'reengagement',
  'converted',
];

export function TemplateForm({
  mode,
  initial,
  submitting,
  availableTemplates = [],
  currentTemplateId,
  submittedToMeta,
  onSubmit,
  onCancel,
}: Props) {
  const [form, setForm] = useState<TemplateFormValue>(initial ?? DEFAULT_FORM_VALUE);
  const [nameError, setNameError] = useState<string | null>(null);
  const [variablesError, setVariablesError] = useState<string | null>(null);
  // Story 7.28 (UX fix) — só revelamos erro de botão depois que o usuário
  // tentou submeter; antes disso, campos intocados não "gritam" obrigatório.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Story 7.26 — preview missing examples in real time so the operator sees
  // the gate before clicking submit. Recomputed every render via useMemo so
  // typing in the body OR in any example cell updates the indicator.
  const missingExamplePositions = useMemo(
    () => findMissingExamplePositions(form.contentPreview, form.variables),
    [form.contentPreview, form.variables],
  );

  const buttonErrors = useMemo(() => form.buttons.map(validateButton), [form.buttons]);
  const hasButtonError = buttonErrors.some(
    (e) =>
      e.text !== null || e.url !== null || e.linkedTemplateId !== null || e.guidanceText !== null,
  );

  const selectableTemplates = availableTemplates.filter((t) => t.id !== currentTemplateId);

  // Story 7.28 (UX fix) — exibe o erro de um campo de botão só quando faz
  // sentido: se o campo tem conteúdo inválido (feedback imediato útil) OU se
  // já houve tentativa de submit (aí mostramos os obrigatórios pendentes).
  // Campo vazio + intocado NÃO mostra erro.
  function shouldShowButtonError(value: string, error: string | null): boolean {
    return error !== null && (value.trim().length > 0 || submitAttempted);
  }

  function patch<K extends keyof TemplateFormValue>(key: K, value: TemplateFormValue[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear the variables error optimistically when the user edits; the
    // memo above is the source of truth for "is the form ready".
    if (variablesError) setVariablesError(null);
  }

  function addButton(type: TemplateButtonType = 'URL') {
    setForm((prev) => {
      if (prev.buttons.length >= URL_BUTTON_MAX_COUNT) return prev;
      const newBtn: TemplateButtonValue =
        type === 'URL'
          ? { type: 'URL', text: '', url: '' }
          : type === 'QUICK_REPLY'
            ? { type: 'QUICK_REPLY', text: '', linkedTemplateId: '' }
            : { type: 'AI_RESPONSE', text: '', guidanceText: '' };
      return { ...prev, buttons: [...prev.buttons, newBtn] };
    });
  }

  function removeButton(index: number) {
    setForm((prev) => ({ ...prev, buttons: prev.buttons.filter((_, i) => i !== index) }));
  }

  function changeButtonType(index: number, newType: TemplateButtonType) {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, i): TemplateButtonValue => {
        if (i !== index) return b;
        if (newType === 'URL') return { type: 'URL', text: b.text, url: '' };
        if (newType === 'QUICK_REPLY') {
          return { type: 'QUICK_REPLY', text: b.text, linkedTemplateId: '' };
        }
        return { type: 'AI_RESPONSE', text: b.text, guidanceText: '' };
      }),
    }));
  }

  function patchButton(index: number, key: string, value: string) {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, i) => (i === index ? { ...b, [key]: value } : b)),
    }));
  }

  function handleNameChange(next: string) {
    patch('metaTemplateName', next);
    if (next === '') {
      setNameError(null);
      return;
    }
    if (!META_TEMPLATE_NAME_RE.test(next)) {
      setNameError('Use apenas letras minúsculas, dígitos e underscore (1-64 caracteres).');
    } else {
      setNameError(null);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Revela os erros de botão pendentes (obrigatórios) na 1ª tentativa.
    setSubmitAttempted(true);

    // Final regex gate — on 'edit' the field is disabled, so we trust the
    // value we loaded from the server.
    if (mode === 'create' && !META_TEMPLATE_NAME_RE.test(form.metaTemplateName)) {
      setNameError('Use apenas letras minúsculas, dígitos e underscore (1-64 caracteres).');
      return;
    }

    // Story 7.26 — gate submit when {{N}} placeholders lack examples. Same
    // rule as the server (`TemplateVariableExamplesMissingError` → 422),
    // applied here to avoid a doomed roundtrip on the happy-path UI.
    if (mode === 'create' && missingExamplePositions.length > 0) {
      const formatted = missingExamplePositions.map((p) => `{{${p}}}`).join(', ');
      const noun = missingExamplePositions.length === 1 ? 'A variável' : 'As variáveis';
      const verb = missingExamplePositions.length === 1 ? 'precisa' : 'precisam';
      setVariablesError(
        `${noun} ${formatted} ${verb} de exemplo antes de submeter à Meta. Preencha o campo "Exemplo" abaixo.`,
      );
      return;
    }

    // Story 7.28 — bloqueia submit com botão inválido (text vazio/>25, url não-https).
    if (hasButtonError) {
      return;
    }

    onSubmit(form);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="space-y-1">
        <Label htmlFor="meta-template-name">Nome do template (Meta)</Label>
        <Input
          id="meta-template-name"
          autoComplete="off"
          value={form.metaTemplateName}
          disabled={mode === 'edit' || submitting}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="ex.: welcome_v1"
          aria-invalid={nameError ? 'true' : undefined}
        />
        {nameError ? (
          <p role="alert" className="text-xs text-destructive">
            {nameError}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Convenção Meta: lowercase, dígitos e underscore (1-64).
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="language-code">Language</Label>
          <Input
            id="language-code"
            value={form.languageCode}
            onChange={(e) => patch('languageCode', e.target.value)}
            disabled={submitting || submittedToMeta}
            placeholder="pt_BR"
          />
          {submittedToMeta ? (
            <p className="text-xs text-muted-foreground">
              Travado após primeiro envio à Meta. Para alterar, crie um novo template.
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="category">Categoria</Label>
          <select
            id="category"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={form.category ?? ''}
            onChange={(e) => patch('category', (e.target.value || null) as TemplateCategory | null)}
            disabled={submitting}
          >
            <option value="">—</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="purpose">Propósito</Label>
          <select
            id="purpose"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={form.purpose}
            onChange={(e) => patch('purpose', e.target.value as TemplatePurpose)}
            disabled={submitting}
          >
            {PURPOSE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="status">Status</Label>
          <select
            id="status"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={form.status}
            onChange={(e) => patch('status', e.target.value as TemplateStatus)}
            disabled={submitting}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="content-preview">
          Texto da mensagem (Body){mode === 'create' ? ' *' : ''}
        </Label>
        <textarea
          id="content-preview"
          className="flex min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          value={form.contentPreview ?? ''}
          onChange={(e) => patch('contentPreview', e.target.value)}
          disabled={submitting}
          placeholder="Olá {{1}}, sua assinatura do {{2}} está pronta..."
        />
        <p className="text-xs text-muted-foreground">
          {mode === 'create' ? (
            <>
              Em <strong>criar</strong>, esse texto é enviado para a Meta como Body do template e
              fica em status <em>pending</em> até a Meta aprovar. Use {`{{1}}`}, {`{{2}}`}... para
              variáveis dinâmicas.
            </>
          ) : (
            <>
              Use {`{{1}}`}, {`{{2}}`}... para variáveis dinâmicas.
            </>
          )}
        </p>
      </div>

      <VariablesEditor
        id="variables-editor"
        value={form.variables}
        contentPreview={form.contentPreview}
        onChange={(next) => patch('variables', next)}
        disabled={submitting}
      />

      {variablesError ? (
        <p role="alert" data-testid="variables-error" className="text-xs text-destructive">
          {variablesError}
        </p>
      ) : null}

      {/* Editor de botões — URL estático ou QUICK_REPLY (envia template). */}
      <div className="space-y-2" data-testid="buttons-editor">
        <div className="flex items-center justify-between">
          <Label>Botões (até {URL_BUTTON_MAX_COUNT})</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addButton('URL')}
              disabled={submitting || form.buttons.length >= URL_BUTTON_MAX_COUNT}
              data-testid="add-button-url"
            >
              + URL
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addButton('QUICK_REPLY')}
              disabled={submitting || form.buttons.length >= URL_BUTTON_MAX_COUNT}
              data-testid="add-button-qr"
            >
              + Enviar template
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addButton('AI_RESPONSE')}
              disabled={submitting || form.buttons.length >= URL_BUTTON_MAX_COUNT}
              data-testid="add-button-ai"
            >
              + Resposta da IA
            </Button>
          </div>
        </div>
        {form.buttons.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Opcional. Adicione até {URL_BUTTON_MAX_COUNT} botões. <strong>URL</strong>: abre um
            link. <strong>Enviar template</strong>: ao clicar, a Rafaela envia outro template
            automaticamente. <strong>Resposta da IA</strong>: ao clicar, a Rafaela gera uma mensagem
            livre guiada pelo texto-base configurado (não é enviada verbatim).
          </p>
        ) : (
          <div className="space-y-3">
            {form.buttons.map((button, index) => {
              const err = buttonErrors[index];
              const showText = shouldShowButtonError(button.text, err?.text ?? null);
              const textErrId = `button-text-${index}-error`;
              return (
                <div
                  key={index}
                  className="space-y-2 rounded-md border border-input p-3"
                  data-testid={`button-row-${index}`}
                >
                  {/* Cabeçalho: tipo + remover */}
                  <div className="flex items-center justify-between">
                    <select
                      value={button.type}
                      onChange={(e) =>
                        changeButtonType(index, e.target.value as TemplateButtonType)
                      }
                      disabled={submitting}
                      className="h-7 rounded border border-input bg-background px-2 text-xs"
                      data-testid={`button-type-${index}`}
                    >
                      <option value="URL">URL (link fixo)</option>
                      <option value="QUICK_REPLY">Enviar template</option>
                      <option value="AI_RESPONSE">Resposta da IA</option>
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeButton(index)}
                      disabled={submitting}
                      aria-label={`Remover botão ${index + 1}`}
                      data-testid={`remove-button-${index}`}
                    >
                      Remover
                    </Button>
                  </div>

                  {/* Texto do botão */}
                  <div className="space-y-1">
                    <Label htmlFor={`button-text-${index}`} className="text-xs">
                      Texto ({button.text.length}/{URL_BUTTON_TEXT_MAX_LEN})
                    </Label>
                    <Input
                      id={`button-text-${index}`}
                      value={button.text}
                      maxLength={URL_BUTTON_TEXT_MAX_LEN}
                      onChange={(e) => patchButton(index, 'text', e.target.value)}
                      disabled={submitting}
                      placeholder={
                        button.type === 'URL'
                          ? 'Acessar plataforma'
                          : button.type === 'QUICK_REPLY'
                            ? 'Sim, quero!'
                            : 'Quero saber mais'
                      }
                      aria-invalid={showText ? 'true' : undefined}
                      aria-describedby={showText ? textErrId : undefined}
                    />
                    {showText ? (
                      <p id={textErrId} className="text-xs text-destructive">
                        {err?.text}
                      </p>
                    ) : null}
                  </div>

                  {/* Campo específico por tipo */}
                  {button.type === 'URL' ? (
                    <div className="space-y-1">
                      {(() => {
                        const showUrl = shouldShowButtonError(button.url, err?.url ?? null);
                        const urlErrId = `button-url-${index}-error`;
                        return (
                          <>
                            <Label htmlFor={`button-url-${index}`} className="text-xs">
                              URL
                            </Label>
                            <Input
                              id={`button-url-${index}`}
                              value={button.url}
                              onChange={(e) => patchButton(index, 'url', e.target.value)}
                              disabled={submitting}
                              placeholder="https://app.exemplo.com/acesso"
                              aria-invalid={showUrl ? 'true' : undefined}
                              aria-describedby={showUrl ? urlErrId : undefined}
                            />
                            {showUrl ? (
                              <p id={urlErrId} className="text-xs text-destructive">
                                {err?.url}
                              </p>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  ) : button.type === 'QUICK_REPLY' ? (
                    <div className="space-y-1">
                      {(() => {
                        const showLinked = shouldShowButtonError(
                          button.linkedTemplateId,
                          err?.linkedTemplateId ?? null,
                        );
                        const linkedErrId = `button-linked-${index}-error`;
                        return (
                          <>
                            <Label htmlFor={`button-linked-${index}`} className="text-xs">
                              Template a enviar quando clicado
                            </Label>
                            <select
                              id={`button-linked-${index}`}
                              value={button.linkedTemplateId}
                              onChange={(e) =>
                                patchButton(index, 'linkedTemplateId', e.target.value)
                              }
                              disabled={submitting}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              aria-invalid={showLinked ? 'true' : undefined}
                              aria-describedby={showLinked ? linkedErrId : undefined}
                            >
                              <option value="">Selecione um template…</option>
                              {selectableTemplates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.metaTemplateName}
                                </option>
                              ))}
                            </select>
                            {showLinked ? (
                              <p id={linkedErrId} className="text-xs text-destructive">
                                {err?.linkedTemplateId}
                              </p>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {(() => {
                        const showGuidance = shouldShowButtonError(
                          button.guidanceText,
                          err?.guidanceText ?? null,
                        );
                        const guidanceErrId = `button-guidance-${index}-error`;
                        return (
                          <>
                            <Label htmlFor={`button-guidance-${index}`} className="text-xs">
                              Texto-guia para a IA ({button.guidanceText.length}/
                              {AI_RESPONSE_GUIDANCE_MAX_LEN})
                            </Label>
                            <textarea
                              id={`button-guidance-${index}`}
                              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              value={button.guidanceText}
                              maxLength={AI_RESPONSE_GUIDANCE_MAX_LEN}
                              onChange={(e) => patchButton(index, 'guidanceText', e.target.value)}
                              disabled={submitting}
                              placeholder="Ex.: dê boas-vindas calorosas e ofereça ajuda com o primeiro acesso à plataforma"
                              aria-invalid={showGuidance ? 'true' : undefined}
                              aria-describedby={showGuidance ? guidanceErrId : undefined}
                            />
                            <p className="text-xs text-muted-foreground">
                              A Rafaela usa esse texto como exemplo/guia — não manda verbatim,
                              adapta ao contexto real da conversa.
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Variáveis disponíveis (sintaxe{' '}
                              <code>
                                {'{{'}variavel{'}}'}
                              </code>
                              , trocadas pelo valor real antes de chegar na IA — sem valor conhecido
                              vira vazio): <code>{'{{customer_name}}'}</code>,{' '}
                              <code>{'{{product_name}}'}</code>, <code>{'{{access_link}}'}</code>,{' '}
                              <code>{'{{payment_link}}'}</code>, <code>{'{{discount_link}}'}</code>,{' '}
                              <code>{'{{special_video_link}}'}</code>,{' '}
                              <code>{'{{first_access_url}}'}</code>, <code>{'{{plan}}'}</code>.{' '}
                              <code>{'{{access_link}}'}</code> e{' '}
                              <code>{'{{special_video_link}}'}</code> variam pelo plano do cliente (
                              <code>start</code>/<code>premium</code>) quando esse dado foi enviado
                              no webhook de trigger.
                            </p>
                            {showGuidance ? (
                              <p id={guidanceErrId} className="text-xs text-destructive">
                                {err?.guidanceText}
                              </p>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Label>Preview</Label>
        <TemplatePreview content={form.contentPreview} variables={form.variables} />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            submitting || !!nameError || (mode === 'create' && missingExamplePositions.length > 0)
          }
        >
          {submitting ? 'Salvando…' : mode === 'create' ? 'Criar template' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
